/**
 * DAO Voting (Admin Web) - constants per SRS "DAO - Admin Web" (Confluence MN).
 */

/** Asset-level status of a PTO project DAO (SRS 3.3.a) - values follow the BE API */
export enum DaoAssetStatusEnum {
  HOLDING = 'HOLDING',
  VOTING = 'VOTING',
  AWAITING_SALE_RESULT = 'AWAITING_SALE_RESULT',
  SALE_FAILED = 'SALE_FAILED',
  SALE_SUCCESS = 'SALE_SUCCESS',
  REFUND_PERIOD = 'REFUND_PERIOD',
  REWARD_PERIOD = 'REWARD_PERIOD',
  ENDED = 'ENDED',
}

/** Proposal-level status (SRS 3.3.b) - values follow the BE API */
export enum DaoProposalStatusEnum {
  DRAFT = 'DRAFT',
  /** published in BE, on-chain propose tx not confirmed yet */
  PUBLISHING = 'PUBLISHING',
  SCHEDULED = 'SCHEDULED',
  ACTIVE = 'ACTIVE',
  PASSED = 'PASSED',
  REJECTED = 'REJECTED',
  /** PTO ERC-3643: delete requested, on-chain cancel tx not confirmed yet */
  DELETING = 'DELETING',
}

export enum DaoPoolTypeEnum {
  REFUND = 'refund',
  REWARDS = 'rewards',
}

/** poolStatus of the Pool Detail block (SRS 5.2) */
export enum DaoPoolStatusEnum {
  SCHEDULED = 'SCHEDULED',
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}

export const DAO_POOL_STATUS_CONFIG: Record<DaoPoolStatusEnum, { label: string; color: string }> = {
  [DaoPoolStatusEnum.SCHEDULED]: { label: 'Scheduled', color: 'default' },
  [DaoPoolStatusEnum.OPEN]: { label: 'Open', color: 'green' },
  [DaoPoolStatusEnum.CLOSED]: { label: 'Closed', color: 'default' },
}

/** poolType as the BE API returns it (uppercase) */
export const DAO_POOL_API_TYPE_LABEL: Record<'REFUND' | 'RWA_REWARDS', string> = {
  REFUND: 'Refund Pool',
  RWA_REWARDS: 'RWA Rewards Pool',
}

export enum DaoSaleOutcomeEnum {
  SUCCESS = 'success',
  FAILED = 'failed',
}

/** Voter support of GET /admin/dao-voting/proposals/:id/voters */
export enum DaoVoteSupportEnum {
  FOR = 'FOR',
  AGAINST = 'AGAINST',
}

/** Milestone status of GET /admin/dao-voting/proposals/:id/timeline */
export enum DaoTimelineStatusEnum {
  DONE = 'DONE',
  ACTIVE = 'ACTIVE',
  UPCOMING = 'UPCOMING',
}

/** Unit of PATCH /admin/dao-voting/proposals/:id/estimated-sale-time */
export enum DaoSaleTimeUnitEnum {
  DAYS = 'DAYS',
  WEEKS = 'WEEKS',
  MONTHS = 'MONTHS',
}

export const DAO_SALE_TIME_UNIT_OPTIONS = [
  { value: DaoSaleTimeUnitEnum.DAYS, label: 'Days' },
  { value: DaoSaleTimeUnitEnum.WEEKS, label: 'Weeks' },
  { value: DaoSaleTimeUnitEnum.MONTHS, label: 'Months' },
]

export const SALE_TIME_UNIT_TO_DAYJS: Record<DaoSaleTimeUnitEnum, 'day' | 'week' | 'month'> = {
  [DaoSaleTimeUnitEnum.DAYS]: 'day',
  [DaoSaleTimeUnitEnum.WEEKS]: 'week',
  [DaoSaleTimeUnitEnum.MONTHS]: 'month',
}

export const DAO_ASSET_STATUS_CONFIG: Record<DaoAssetStatusEnum, { label: string; color: string }> =
  {
    [DaoAssetStatusEnum.HOLDING]: { label: 'Holding', color: 'default' },
    [DaoAssetStatusEnum.VOTING]: { label: 'Voting', color: 'purple' },
    [DaoAssetStatusEnum.AWAITING_SALE_RESULT]: { label: 'Awaiting Sale Result', color: 'orange' },
    [DaoAssetStatusEnum.SALE_FAILED]: { label: 'Sale Failed', color: 'gold' },
    [DaoAssetStatusEnum.SALE_SUCCESS]: { label: 'Sale Success', color: 'green' },
    [DaoAssetStatusEnum.REFUND_PERIOD]: { label: 'Refund Period', color: 'default' },
    [DaoAssetStatusEnum.REWARD_PERIOD]: { label: 'Reward Period', color: 'green' },
    [DaoAssetStatusEnum.ENDED]: { label: 'Ended', color: 'default' },
  }

export const DAO_PROPOSAL_STATUS_CONFIG: Record<
  DaoProposalStatusEnum,
  { label: string; color: string }
> = {
  [DaoProposalStatusEnum.DRAFT]: { label: 'Draft', color: 'gold' },
  [DaoProposalStatusEnum.PUBLISHING]: { label: 'Publishing', color: 'processing' },
  [DaoProposalStatusEnum.SCHEDULED]: { label: 'Scheduled', color: 'default' },
  [DaoProposalStatusEnum.ACTIVE]: { label: 'Active', color: 'purple' },
  [DaoProposalStatusEnum.PASSED]: { label: 'Passed', color: 'green' },
  [DaoProposalStatusEnum.REJECTED]: { label: 'Rejected', color: 'error' },
  [DaoProposalStatusEnum.DELETING]: { label: 'Deleting', color: 'processing' },
}

export const DAO_POOL_TYPE_LABEL: Record<DaoPoolTypeEnum, string> = {
  [DaoPoolTypeEnum.REFUND]: 'Refund Pool',
  [DaoPoolTypeEnum.REWARDS]: 'RWA Rewards Pool',
}

export const DAO_ASSET_STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Status' },
  ...Object.values(DaoAssetStatusEnum).map((status) => ({
    value: status,
    label: DAO_ASSET_STATUS_CONFIG[status].label,
  })),
]

/** tokenize.sellingVersion - PTO generation; filter of GET /admin/dao-voting/pto-projects */
export enum DaoSellingVersionEnum {
  DIRECT = 1,
  POOL = 2,
  ERC3643 = 3,
}

export const DAO_SELLING_VERSION_FILTER_OPTIONS = [
  { value: '', label: 'All Versions' },
  { value: DaoSellingVersionEnum.DIRECT, label: 'PTO v1' },
  { value: DaoSellingVersionEnum.POOL, label: 'PTO v2' },
  { value: DaoSellingVersionEnum.ERC3643, label: 'PTO ERC-3643' },
]

/** SRS 4 - Messages */
export const DAO_MESSAGES = {
  E01: (field: string, min = 1) => `${field} cannot be smaller than ${min}`,
  E02: (field: string) => `${field} cannot be greater than 100`,
  E03: 'End time must be greater than start time',
  E04: 'Start time cannot be in the past',
  E05: 'Please enter a valid wallet address starting with 0x',
  E06: (field: string) => `${field} must be greater than 0`,
  E07: 'Claim deadline cannot be in the past',
  S01: 'Publish proposal successfully',
  S02: 'Save proposal as a draft successfully',
  S03: 'Update proposal successfully',
  S04: 'Delete proposal successfully',
  S05: 'Record sale result successfully',
  S06: 'Create refund pool successfully',
  S07: 'Create rewards pool successfully',
}

/** `data.error` codes of the DAO admin APIs (DAO_GOVERNANCE_ERC3643_FE_NOTE 2, 6) */
export const DAO_API_ERROR_MESSAGES: Record<string, string> = {
  DAO_NO_VOTING_POWER: 'This PTO has no holders to vote yet.',
  DAO_ASSET_NOT_HOLDING: 'Please Update Status back to Holding before publishing a new proposal.',
  DAO_ACTIVE_PROPOSAL_EXISTS: 'This PTO already has a proposal that has not ended yet.',
  DAO_SALE_NOT_STARTED:
    'Waiting for the vote result to be executed on-chain. Please try again in a few minutes.',
  DAO_ASSET_STATE_MISMATCH:
    'Pool type does not match the sale result: a refund pool needs Sale Failed, a rewards pool needs Sale Success.',
  DAO_POOL_ALREADY_EXISTS: 'This PTO already has a pool on-chain. Each PTO can only have one pool.',
}

/** SRS 3.1 #8 - datetime display format */
export const DAO_DATETIME_FORMAT = 'MMM DD, YYYY, h:mm A'
export const DAO_DATE_FORMAT = 'MMM DD, YYYY'

/** SRS 3.1 #4 - pagination options */
export const DAO_PAGE_SIZE_OPTIONS = ['10', '20', '50']
